<template>
    <div>
        <CheckIfHorseExistDialog v-model="showExistingHorsesDialog" :similarHorses="similarHorses"
                                 external-horse @useHorse="useHorse" :disclaimer="$t('message.horseEdit.covering.externalStallionExistsDisclaimer')"/>
        <VeeForm @submit="saveCertificate" v-slot="{ isSubmitting }">
            <div class="flex flex-column gap-5">
                <div class="flex flex-column gap-3 w-full align-items-center">
                    <span>{{$t('message.horseEdit.covering.externalStallionDisclaimer')}}</span>
                    <CustomInputText
                        name="name"
                        class="w-8"
                        :label="$t('message.horseEdit.main.pedigree.name')"
                        required
                        v-model="newCertificate.name"
                        noPlaceholder
                        rules="required"
                        force-uppercase/>

                    <CustomSelectOneHorseSex
                        name="sex"
                        class="w-8"
                        :label="$t('message.horseEdit.main.pedigree.gender')"
                        :disabled="true"
                        v-model="newCertificate.sex"
                        noPlaceholder
                        required
                        rules="required"/>

                    <CustomSelectOneHorseColor
                        name="colorId"
                        class="w-8"
                        :label="$t('message.horseEdit.main.pedigree.coatColor')"
                        v-model="newCertificate.color"
                        noPlaceholder/>

                    <CustomCalendar
                        name="dateOfBirth"
                        class="w-8"
                        :label="$t('message.horseEdit.main.pedigree.dateOfBirth')"
                        v-model="newCertificate.dateOfBirth"
                        :max-date="today"/>

                    <CustomInputText
                        name="ueln"
                        class="w-8"
                        :label="$t('message.horseEdit.main.pedigree.ueln')"
                        v-model="newCertificate.ueln"
                        noPlaceholder
                        :max-length="15"/>

                    <CustomSelectOneCountry
                        name="countryOfBirthId"
                        class="w-8"
                        :label="$t('message.horseEdit.main.pedigree.countryOfBirth')"
                        v-model="newCertificate.countryOfBirthId"
                        itemValue="id"
                        filter
                    />

                    <HorseEditMainPedigree v-model="newCertificate" style="min-width: 100%"/>

                    <CustomButton
                        type="submit"
                        :label="newCertificate.externalHorseId ? $t('message.horseEdit.covering.addExternalStallionAndUseCertificate') : $t('message.horseEdit.covering.addExternalStallionAndCertificate')"
                        :disabled="isSubmitting"
                    />
                </div>
            </div>
        </VeeForm>
    </div>
</template>

<script>
import _ from "lodash"
import CustomButton from '@/components/form/CustomButton.vue';
import {
    createCoveringCertificateForExternalStallion, searchSimpleHorse,
} from "@/swagger/vue-api-client";
import { Form as VeeForm } from 'vee-validate';
import PedigreeAddHorseForm from "@/views/horse/horseEdit/main/components/PedigreeAddHorseForm.vue";
import CustomInputText from "@/components/form/CustomInputText.vue";
import CustomSelectOneHorseSex from "@/components/form/CustomSelectOneHorseSex.vue";
import CustomSelectOneCountry from "@/components/form/CustomSelectOneCountry.vue";
import CustomSelectOneHorseColor from "@/components/form/CustomSelectOneHorseColor.vue";
import CustomCalendar from "@/components/form/CustomCalendar.vue";
import CheckIfHorseExistDialog from "@/components/CheckIfHorseExistDialog.vue";
import HorseEditMainPedigree
    from "@/views/horse/horseEdit/main/components/HorseEditMainPedigree.vue";


export default {
    name: "NewCertificateDialogExternalStallionTab",

    components: {
        HorseEditMainPedigree,
        CheckIfHorseExistDialog,
        CustomCalendar,
        CustomSelectOneHorseColor,
        CustomSelectOneCountry,
        CustomSelectOneHorseSex,
        CustomInputText,
        PedigreeAddHorseForm,
        VeeForm,
        CustomButton,
    },

    props: {
        modelValue: null,
        horseName: {
            type: String,
            default: "",
        },
    },

    emits: ["search", "update:modelValue", "refreshHorse"],

    data() {
        return {
            newCertificate: this.getClearNewExternalStallion(),
            showExistingHorsesDialog: false,
            dialogShowed: false,
            similarHorses: []
        };
    },

    methods: {
        async saveCertificate() {
            try {
                await searchSimpleHorse({body:
                        {
                            name: this.newCertificate.name,
                            ueln: this.newCertificate.ueln,
                            birthCountryId: this.newCertificate.countryOfBirthId,
                            dateOfBirth: {min: this.newCertificate.dateOfBirth, max: this.newCertificate.dateOfBirth},
                            sex: "STALLION",
                            horseBreed: localStorage.getItem("horseBreed"),
                            horseStatus: "EXTERNAL"
                        }})
                    .then((response) => {
                        this.similarHorses = response.data;
                    });
                if (!this.newCertificate.externalHorseId && this.similarHorses.length > 0 && !this.dialogShowed) {
                    this.showExistingHorsesDialog = true;
                    this.dialogShowed = true;
                    return;
                }


                const newCertificateRequest = _.cloneDeep(this.newCertificate);

                await createCoveringCertificateForExternalStallion({mareId: this.$route.params.id, body: newCertificateRequest});

                this.$emit("search");
                this.$emit("refreshHorse");
                this.newCertificate = this.getClearNewExternalStallion();
                this.internalValue = false;
                this.notifySaved();
            } catch (err) {
                this.notifyError(err);
            }
        },

        useHorse(value) {
            this.newCertificate.externalHorseId = value.id;
            this.showExistingHorsesDialog = false;
        },

        getClearNewExternalStallion() {
            return {
                mareId: this.$route.params.id,
                name: null,
                sex: 'STALLION',
                color: null,
                dateOfBirth: null,
                ueln: null,
                countryOfBirthId: null,
                father: null,
                mother: null,
            };
        },

        notifySaved() {
            this.$toast.add({
                severity: "success",
                summary: this.$t('message.other.success'),
                detail: this.$t('message.other.dataSaved'),
                life: 3000,
            });
        },

        notifyError(error) {
            const message = error.response?.data?.message || this.$t('unknownError');
            this.$toast.add({
                severity: "error",
                summary: this.$t('message.other.error'),
                detail: message,
                life: 5000,
            });
        },
    },

        computed: {
            internalValue: {
                get() {
                    return this.modelValue;
                },
                set(value) {
                    this.$emit("update:modelValue", value);
                },
            },
        },

        watch: {
        },
    };
</script>

<style lang="scss">
    .new-certificate-dialog {
        .custom-input-text, .custom-input-number, .custom-select-one, .custom-calendar, .custom-input-textarea {
            padding-left: 170px;
            .custom-label {
                z-index: 999;
                position: absolute;
                transform: translateX(-100%);
                left: 160px;
                text-align: right;
            }
        }
    }
</style>
